// src/components/AnnualReport.js
// State Annual Report / Franchise Tax dashboard.
// FREE FOR EVERYONE — no plan/credit gating, no submitTool call, no
// filing_history record. The due-date countdown, calendar, and step
// checklist are computed instantly from state + formation date. Entered
// info is saved to Firestore (annualReportInfo/{uid}) for logged-in users,
// same as the rest of the account, so it follows them across devices and
// survives tab switches/reloads — not just cached in this one browser.
// Logged-out guests fall back to localStorage since there's no account to
// attach data to. (The old "email me a PDF + calendar file" flow — and the
// server/annualReport.js route + emailPacket/buildReminderPacket/buildIcs
// it used — is no longer called from here; that route is now dead code
// unless something else still hits it.)
//
// The right-side dashboard (countdown / calendar / steps) now renders
// immediately on load with "—" placeholders instead of waiting for the
// left-side form to be filled in — it fills in with real numbers the
// moment a formation state (and, for anniversary-based states, a
// formation month) is chosen.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { STATE_RULES, computeDueDate } from '../utils/annualReportDates';

const SUPPORTED_STATES = [
  { code: 'DE', name: 'Delaware' },
  { code: 'WY', name: 'Wyoming' },
];

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const EMPTY = {
  llcName: '', formationState: '', formationDate: currentMonthStr(),
};

const URGENCY_STYLES = {
  critical: { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B', label: 'Due soon' },
  soon:     { bg: '#FFFBEB', border: '#F59E0B', text: '#92400E', label: 'Coming up' },
  ok:       { bg: '#F0FDF4', border: '#22C894', text: '#166534', label: 'On track' },
  // Shown before a formation state is picked — same card, dashes instead of numbers.
  pending:  { bg: '#F9FAFB', border: '#E5E7EB', text: '#6B7280', label: 'Add your info' },
};

const HOW_IT_WORKS_STEPS = [
  "Select your LLC's formation state from the dropdown on the left.",
  "Add your formation month if it's asked for — Wyoming's due date depends on it; Delaware's is fixed by statute either way.",
  'Your countdown, due date, and calendar fill in instantly — nothing to submit, nothing saved until you type.',
  "When you're ready, follow the 3 steps on the right to file directly with the state.",
];

function storageKey(user) {
  return `annualReportForm:${user?.uid || 'guest'}`;
}

function CountdownCard({ rule, dueDateISO, daysRemaining, urgency }) {
  const isPending = !rule || !dueDateISO;
  const style = URGENCY_STYLES[urgency] || URGENCY_STYLES.pending;

  const dueDateStr = dueDateISO
    ? new Date(dueDateISO + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div style={{
      background: style.bg, border: `1.5px solid ${style.border}`,
      borderRadius: 12, padding: 20, marginBottom: 20,
      display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <div style={{ textAlign: 'center', minWidth: 90 }}>
        <div style={{ fontSize: 40, fontWeight: 800, color: style.text, lineHeight: 1 }}>
          {daysRemaining !== null && daysRemaining !== undefined ? daysRemaining : '—'}
        </div>
        <div style={{ fontSize: 12, color: style.text, fontWeight: 600, marginTop: 4 }}>
          days left
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: style.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {style.label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1B2B4B', marginTop: 4 }}>
          {rule ? `${rule.name} ${rule.label}` : 'Select your formation state'}
        </div>
        <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
          {isPending
            ? 'Pick a state on the left to see your fee and due date'
            : `${rule.fee} · Due ${dueDateStr}`}
        </div>
      </div>
    </div>
  );
}

function MiniCalendar({ dueDateISO }) {
  // No due date yet — fall back to today's month so the calendar still
  // renders, just without a highlighted due day.
  const due = dueDateISO ? new Date(dueDateISO + 'T00:00:00') : new Date();
  const year = due.getFullYear();
  const month = due.getMonth();
  const today = new Date();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = due.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isDueDay = (d) => Boolean(dueDateISO) && d === due.getDate();
  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div className="ar-calendar">
      <div className="ar-calendar-header">{monthName}</div>
      <div className="ar-calendar-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="ar-calendar-dow">{d}</div>
        ))}
        {cells.map((d, i) => (
          <div
            key={i}
            className={
              'ar-calendar-cell' +
              (d === null ? ' ar-calendar-cell--empty' : '') +
              (d && isDueDay(d) ? ' ar-calendar-cell--due' : '') +
              (d && isToday(d) ? ' ar-calendar-cell--today' : '')
            }
          >
            {d || ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepChecklist({ rule }) {
  if (!rule) {
    // Generic placeholder steps shown before a state is picked.
    const placeholderSteps = [
      'Select your formation state on the left',
      "Add your formation month, if it's asked for",
      "We'll show your state's exact filing steps and portal link here",
    ];
    return (
      <div className="ar-steps">
        {placeholderSteps.map((text, i) => (
          <div key={i} className="ar-step-row">
            <div className="ar-step-num" style={{ background: '#9CA3AF' }}>{i + 1}</div>
            <div className="ar-step-text" style={{ color: '#9CA3AF' }}>{text}</div>
          </div>
        ))}
      </div>
    );
  }

  const steps = [
    { text: `Go to the ${rule.name} filing portal`, link: rule.portalUrl },
    { text: 'Have your state file/entity number ready (on your Certificate of Formation)' },
    { text: "Pay by card — save the state's emailed receipt as your proof of filing" },
  ];

  return (
    <div className="ar-steps">
      {steps.map((s, i) => (
        <div key={i} className="ar-step-row">
          <div className="ar-step-num">{i + 1}</div>
          <div className="ar-step-text">
            {s.text}
            {s.link && (
              <a href={s.link} target="_blank" rel="noopener noreferrer" className="ar-step-link">
                Open portal →
              </a>
            )}
          </div>
        </div>
      ))}
      <div className="ar-verify-note">
        Fees and deadlines are set by state statute and can change — verify at{' '}
        <a href={rule.verifyUrl} target="_blank" rel="noopener noreferrer">{rule.verifyUrl}</a>{' '}
        before you pay.
      </div>
    </div>
  );
}

export function AnnualReport({ user }) {
  const [form, setForm] = useState(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const saveTimer = useRef(null);

  const normalizeLoaded = (data) => ({
    llcName:        data.llcName || '',
    formationState: data.formationState || '',
    // Older saves may hold a full "YYYY-MM-DD" from when this was a date
    // picker — trim to "YYYY-MM" so the month input can display it.
    // An empty/missing value falls back to the current month rather than
    // showing blank dashes.
    formationDate: data.formationDate ? data.formationDate.slice(0, 7) : currentMonthStr(),
  });

  // Load any previously entered info for this user the moment this tab mounts
  // (it unmounts/remounts on every tab switch, so this is what makes the
  // countdown "stick" instead of resetting to blank each time). Logged-in
  // users load from Firestore — same source of truth as the rest of the
  // account, so it follows them across devices/sessions. Logged-out guests
  // fall back to localStorage since there's no account to attach data to.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (user?.uid) {
          const snap = await getDoc(doc(db, 'annualReportInfo', user.uid));
          if (!cancelled && snap.exists()) {
            setForm({ ...EMPTY, ...normalizeLoaded(snap.data()) });
          }
        } else {
          const saved = localStorage.getItem(storageKey(user));
          if (!cancelled && saved) {
            setForm({ ...EMPTY, ...normalizeLoaded(JSON.parse(saved)) });
          }
        }
      } catch (e) {
        console.error('[AnnualReport] failed to load saved info:', e);
        // non-fatal — just start blank
      } finally {
        if (!cancelled) setHydrated(true);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Persist on every change, but only after the initial load above has run
  // (otherwise this would immediately overwrite saved data with EMPTY on
  // first render, before the load effect gets a chance to run). Firestore
  // writes are debounced so typing an LLC name doesn't fire a write per
  // keystroke — only ~800ms after the user stops changing something.
  useEffect(() => {
    if (!hydrated) return;

    if (user?.uid) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        setDoc(doc(db, 'annualReportInfo', user.uid), {
          llcName:        form.llcName,
          formationState: form.formationState,
          formationDate:  form.formationDate,
          updatedAt:      serverTimestamp(),
        }, { merge: true }).catch(e => console.error('[AnnualReport] failed to save:', e));
      }, 800);
      return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    }

    try {
      localStorage.setItem(storageKey(user), JSON.stringify(form));
    } catch {
      // storage full/blocked — countdown still works for this tab session
    }
  }, [form, hydrated, user]);

  const handle = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const selectedRule = STATE_RULES[form.formationState] || null;

  // Live preview — recomputed instantly on every keystroke, no network call.
  // Returns null (via computeDueDate) until a formation state is picked, at
  // which point the right side swaps its dash placeholders for real values.
  const preview = useMemo(
    () => computeDueDate(form.formationState, form.formationDate),
    [form.formationState, form.formationDate]
  );

  const handleClear = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setForm(EMPTY);
    if (user?.uid) {
      deleteDoc(doc(db, 'annualReportInfo', user.uid)).catch(e =>
        console.error('[AnnualReport] failed to clear saved info:', e)
      );
    } else {
      try { localStorage.removeItem(storageKey(user)); } catch {}
    }
  };

  const hasAnyInput = form.llcName || form.formationState || form.formationDate;

  return (
    <div className="form-page">
      <div className="form-page-inner" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

        {/* ── LEFT: form ── */}
        <div className="form-panel">
          <div className="form-panel-header">
            <span className="form-panel-dot" />
            <span className="form-panel-title">LLC & Deadline Info</span>
          </div>
          <div className="form-panel-body">

            <div className="form-group">
              <label className="form-label">LLC name</label>
              <input className="form-input" type="text" placeholder="Acme Solutions LLC"
                value={form.llcName} onChange={handle('llcName')} />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Formation state</label>
                <select className="form-input form-select"
                  value={form.formationState} onChange={handle('formationState')}>
                  <option value="">Select state</option>
                  {SUPPORTED_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </div>
              {selectedRule?.anniversaryBased && (
                <div className="form-group">
                  <label className="form-label">Formation month</label>
                  <input className="form-input" type="month"
                    value={form.formationDate} onChange={handle('formationDate')} />
                </div>
              )}
            </div>

            {selectedRule && !selectedRule.anniversaryBased && (
              <div style={{ fontSize: 12, color: '#888', marginTop: -8, marginBottom: 8 }}>
                {selectedRule.name}'s due date is fixed by statute — same date for every LLC, so no formation date needed here.
              </div>
            )}

            {hasAnyInput && (
              <button className="fr-btn-secondary" onClick={handleClear} type="button" style={{ marginTop: 8 }}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT: live dashboard — always rendered, dashes until a state is picked ── */}
        <div>
          <CountdownCard
            rule={preview?.rule}
            dueDateISO={preview?.dueDateISO}
            daysRemaining={preview?.daysRemaining}
            urgency={preview?.urgency}
          />
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <MiniCalendar dueDateISO={preview?.dueDateISO} />
            <div style={{ flex: 1, minWidth: 240 }}>
              <StepChecklist rule={preview?.rule} />
            </div>
          </div>
        </div>

        {/* ── BELOW: how this works, spans both columns ── */}
        <div className="fr-card" style={{ gridColumn: '1 / -1' }}>
          <div className="fr-card-title">How this works</div>
          <div className="ar-steps">
            {HOW_IT_WORKS_STEPS.map((text, i) => (
              <div key={i} className="ar-step-row">
                <div className="ar-step-num">{i + 1}</div>
                <div className="ar-step-text">{text}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}